import { createContext, type PropsWithChildren, useMemo } from 'react';
import { Looker40SDK } from '@looker/sdk';
import {
  DefaultSettings,
  type IApiSettings,
  type IRequestProps,
  type ITransport,
  ProxySession,
} from '@looker/sdk-rtl';

import { appConfig } from '@/config';
import { useAuth } from '@/hooks/use-auth';

export const LookerSdkContext = createContext<Looker40SDK>(
  // @ts-expect-error - This is a placeholder value that should never be used
  null,
);

export type LookerSdkProps = PropsWithChildren<{}>;

export function LookerSdk({ children }: LookerSdkProps) {
  const auth = useAuth();

  const coreSdk = useMemo(
    function () {
      const settings = DefaultSettings();
      const session = new GatewaySession(
        auth.getAccessToken,
        `${appConfig.bffUrl}/looker`,
        settings,
      );
      const coreSdk = new Looker40SDK(session);

      return coreSdk;
    },
    [auth.getAccessToken],
  );

  return (
    <LookerSdkContext.Provider value={coreSdk}>
      {children}
    </LookerSdkContext.Provider>
  );
}

class GatewaySession extends ProxySession {
  constructor(
    private getAccessToken: () => Promise<string>,
    public proxyUrl: string,
    public settings: IApiSettings,
    transport?: ITransport,
  ) {
    super(settings, proxyUrl, transport);
  }

  async getToken(): Promise<any> {
    throw new Error('getToken not implemented yet');
  }

  async authenticate(props: IRequestProps) {
    const xLookerPath = props.url;

    const [props2, accessToken] = await Promise.all([
      super.authenticate(props),
      this.getAccessToken(),
    ]);

    props2.headers['authorization'] = `Bearer ${accessToken}`;
    props2.headers['x-looker-path'] = xLookerPath;

    return props2;
  }
}
